import React, { Component } from "react";
import {
    addMilliseconds,
    addMinutes,
    startOfMinute,
    subMilliseconds,
} from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as NumericSetModuleWorker from "worker-loader?name=static/[name].[contenthash:4].js!./NumericSetModule.worker";

import { logger } from "@formant/core/common/logger";
import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { INumericAggregate } from "@formant/core/model/INumericAggregate";
import { INumericSetEntry } from "@formant/core/model/INumericSetEntry";
import { INumericViewConfiguration } from "@formant/core/model/INumericViewConfiguration";
import { IStreamData } from "@formant/core/model/IStreamData";
import { Uuid } from "@formant/core/model/Uuid";

import { IAggregatesSpan } from "../../store/IDeviceConfigurationStore";
import {
    deviceConfigurationStore,
    observeStore,
    queryStore,
} from "../../stores";
import { DeviceSelector } from "../common/DeviceSelector";
import { DeviceTags } from "../common/DeviceTags";
import useModuleMemory from "./hooks/UseModuleMemory";
import { IBaseModuleProps } from "./IBaseModuleProps";
import { Module, ModuleSize } from "./Module";
import { ModuleFooter } from "./ModuleFooter";
import { INumericSetFullEntry, NumericSet } from "./NumericSet";
import { currentByDevice } from "./utils/currentByDevice";
import { findConfiguration } from "./utils/findConfiguration";
import { IMetadataDataPoint } from "./utils/IMetadataDataPoint";

@observer
export class NumericSetModule extends Component<IBaseModuleProps> {
    @observable
    private selectedDeviceId?: Uuid;
    @observable
    private size?: ModuleSize;
    private data: IStreamData<"numeric set">[] = [];

    @observable.ref
    private entries: INumericSetFullEntry[] = [];

    private oldData: IStreamData<"numeric set">[] = [];

    private worker: typeof NumericSetModuleWorker | undefined;

    @observable
    private span = 1 * duration.minute;

    @observable
    private useModuleMemory = useModuleMemory();

    public componentDidMount() {
        const { streamName, configuration } = this.props;

        // check if span value exists in local storage
        const serializedState = this.useModuleMemory.rehydrate(streamName);

        if (serializedState) {
            const { span } = serializedState;
            this.span = span;
        } else {
            const { span } =
                findConfiguration(configuration, streamName, "numeric set") ||
                {};
            if (span) {
                this.span = span;
            }
        }
        if (typeof this.span !== "number") {
            logger.warn("NumericSetModule: span is not a number", {
                span: this.span,
            });
            this.span = 1 * duration.minute;
        }

        this.worker = new NumericSetModuleWorker();
        this.worker.onmessage = ({ data }: any) => {
            const { error, entries } = data as {
                error: string;
                entries: INumericSetFullEntry[];
            };
            if (error) {
                logger.error(error);
            } else {
                this.entries = entries;
            }
        };
    }

    componentWillUnmount() {
        if (this.worker) {
            this.worker.terminate();
        }
    }

    public render() {
        const { selectedDeviceId, size, onSelectDevice } = this;
        const {
            streamName,
            filter,
            hovering,
            fullscreen,
            hideDevice,
            focused,
            onSelectTime,
            layoutModule,
            device,
        } = this.props;
        const { time, mode } = observeStore;
        const { entries } = this;
        const span = this.span;
        const showOnlyCurrentValues =
            layoutModule?.configuration?.showOnlyCurrentValues;

        const live = mode === "live";

        const endTime = addMilliseconds(time, span / 4);

        const startTime = subMilliseconds(time, (span * 3) / 4);
        const queryBuffer = 0.25;
        const queryStartTime = startOfMinute(
            subMilliseconds(startTime, span * queryBuffer),
        );
        const queryEndTime = addMinutes(
            startOfMinute(addMilliseconds(endTime, span * queryBuffer)),
            1,
        );

        let aggregatesSpan: IAggregatesSpan | undefined;
        if (!showOnlyCurrentValues) {
            aggregatesSpan = deviceConfigurationStore.calculateAggregatesSpan(
                queryStartTime,
                queryEndTime,
                span,
                streamName,
                device?.id,
            );
        }

        let queryData:
            | IStreamData<"numeric set">[]
            | "too much data"
            | undefined;

        if (aggregatesSpan) {
            const { aggStart, aggEnd, aggregateLevel } = aggregatesSpan;
            const queryAggregateData = queryStore.moduleQueryAggregates(
                filter,
                streamName,
                "numeric set",
                aggStart,
                aggEnd,
                aggregateLevel,
            );
            if (queryAggregateData === "too much data" || !queryAggregateData) {
                queryData = queryAggregateData;
            } else {
                // based off aggegates lets create artificial data to acccuarately show
                // min max avg
                queryData = queryAggregateData.map(_ => ({
                    deviceId: _.deviceId,
                    name: _.name,
                    type: _.type,
                    tags: _.tags,
                    points: _.aggregates
                        .map(agg => {
                            const aggs = agg[1] as any as {
                                [key in string]: {
                                    value: INumericAggregate;
                                    unit?: string;
                                };
                            };
                            const keys = Object.keys(aggs);
                            const tripplet: [number, INumericSetEntry[]][] = [
                                [
                                    agg[0],
                                    keys.map(label => ({
                                        label,
                                        value: aggs[label].value.min,
                                        unit: aggs[label].unit,
                                    })),
                                ],
                                [
                                    agg[0],
                                    keys.map(label => ({
                                        label,
                                        value:
                                            aggs[label].value.sum /
                                            aggs[label].value.count,
                                        unit: aggs[label].unit,
                                    })),
                                ],
                                [
                                    agg[0],
                                    keys.map(label => ({
                                        label,
                                        value: aggs[label].value.max,
                                        unit: aggs[label].unit,
                                    })),
                                ],
                            ];
                            return tripplet;
                        })
                        .flat(),
                }));
            }
        } else {
            queryData = queryStore.moduleQuery(
                filter,
                streamName,
                "numeric set",
                queryStartTime,
                queryEndTime,
            );
        }

        const loading = queryData === undefined;
        const tooMuchData = queryData === "too much data";

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const currentPoints = currentByDevice(data, time);

        const active = currentPoints.find(
            ({ deviceId }) => selectedDeviceId === deviceId,
        );

        const deviceIds = currentPoints.map(_ => _.deviceId);

        const latestTime = currentPoints.slice(-1)?.[0]?.time.getTime();
        const old = latestTime
            ? latestTime < time.getTime() - 5 * duration.second
            : undefined;

        const empty = data.length === 0;

        if (data !== this.oldData) {
            const all = data.filter(_ => _.deviceId === selectedDeviceId);
            this.sendToWorker({
                all,
                active,
            });
        }

        return (
            <Module
                {...this.props}
                type="numeric set"
                loading={loading}
                empty={empty}
                tooMuchData={tooMuchData}
                onChangeSize={this.onChangeSize}
                onResetTimeSpan={this.onResetTimeSpan}
            >
                {selectedDeviceId && (
                    <NumericSet
                        deviceId={selectedDeviceId}
                        size={size}
                        time={time}
                        live={live}
                        startTime={startTime}
                        endTime={endTime}
                        entries={entries}
                        focused={focused}
                        old={old}
                        onSelectTime={onSelectTime}
                        span={span}
                        onChangeSpan={this.onChangeSpan}
                        showOnlyCurrentValues={showOnlyCurrentValues || false}
                    />
                )}
                <ModuleFooter visible={hovering} floating={true}>
                    {active && selectedDeviceId && !hideDevice && (
                        <DeviceTags
                            deviceId={selectedDeviceId}
                            tags={active.tags}
                        />
                    )}
                    <DeviceSelector
                        selected={selectedDeviceId}
                        deviceIds={deviceIds}
                        onSelect={onSelectDevice}
                        fullscreen={fullscreen}
                    />
                </ModuleFooter>
            </Module>
        );
    }

    private sendToWorker = throttle(
        (data: {
            all: IStreamData<"numeric set">[];
            active: IMetadataDataPoint<"numeric set"> | undefined;
        }) => {
            if (this.worker) {
                this.worker.postMessage(data);
            }
        },
        1000,
    );

    private onResetTimeSpan = () => {
        this.span = 1 * duration.minute;
        this.changeConfiguration({ span: this.span });
    };

    private changeConfiguration(updates: Partial<INumericViewConfiguration>) {
        const { streamName, onChangeConfiguration } = this.props;

        // save to local storage
        this.useModuleMemory.saveState(streamName, updates);

        if (!onChangeConfiguration) {
            return;
        }
        onChangeConfiguration({
            streamName,
            type: "numeric set",
            updates,
        });
    }

    private onChangeSpan = (span: number) => {
        this.span = span;
        this.changeConfiguration({ span });
    };

    private onSelectDevice = (deviceId: Uuid) => {
        this.selectedDeviceId = deviceId;
    };

    private onChangeSize = (size: ModuleSize) => {
        this.size = size;
    };
}
